import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { Experience, ExperienceCheckDomMutation, ExperienceCheckTimeout, containsPopupWithNestedElement, getPopupContainerFromEditorView } from '@atlaskit/editor-common/experiences';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
var pluginKey = new PluginKey('selectionToolbarOpenExperience');
var START_METHOD = {
  MOUSE_UP: 'mouse-up',
  KEY_DOWN: 'key-down'
};
var ABORT_REASON = {
  SELECTION_CLEARED: 'selection-cleared',
  BLOCK_MENU_OPENED: 'block-menu-opened',
  EDITOR_DESTROYED: 'editor-destroyed'
};
/**
 * This experience tracks when the selection toolbar is opened.
 *
 * Start: When user makes a selection via mouseup or shift+arrow key down
 * Success: When the selection toolbar is added to the DOM within 500ms of start
 * Failure: When 500ms passes without the selection toolbar being added to the DOM
 * Abort: When selection transition to empty or block menu is opened
 */
export var getSelectionToolbarOpenExperiencePlugin = function getSelectionToolbarOpenExperiencePlugin(_ref) {
  var refs = _ref.refs;
  var targetEl;
  var editorViewEl;
  var getTarget = function getTarget() {
    if (!targetEl) {
      targetEl = refs.popupsMountPoint || getPopupContainerFromEditorView(editorViewEl);
    }
    return targetEl;
  };
  var experience = new Experience('selection-toolbar-open', {
    checks: [new ExperienceCheckTimeout({
      durationMs: 500,
      onTimeout: function onTimeout() {
        if (isBlockMenuWithinNode(getTarget())) {
          return {
            status: 'abort',
            metadata: {
              reason: ABORT_REASON.BLOCK_MENU_OPENED
            }
          };
        }
      }
    }), new ExperienceCheckDomMutation({
      onDomMutation: function onDomMutation(_ref2) {
        var mutations = _ref2.mutations;
        if (mutations.some(isSelectionToolbarAddedInMutation)) {
          return {
            status: 'success'
          };
        }
      },
      observeConfig: function observeConfig() {
        return {
          target: getTarget(),
          options: {
            childList: true
          }
        };
      }
    })]
  });
  return new SafePlugin({
    key: pluginKey,
    state: {
      init: function init() {
        return {};
      },
      apply: function apply(_tr, pluginState, oldState, newState) {
        if (!oldState.selection.empty && newState.selection.empty) {
          experience.abort({
            metadata: {
              reason: ABORT_REASON.SELECTION_CLEARED
            }
          });
        }
        return pluginState;
      }
    },
    props: {
      handleDOMEvents: {
        mouseup: function mouseup(view) {
          if (!view.state.selection.empty && !isSelectionToolbarWithinNode(getTarget())) {
            experience.start({
              metadata: {
                method: START_METHOD.MOUSE_UP
              }
            });
          }
        },
        keydown: function keydown(_view, _ref3) {
          var shiftKey = _ref3.shiftKey,
            key = _ref3.key;
          if (shiftKey && key.includes('Arrow') && !isSelectionToolbarWithinNode(getTarget())) {
            experience.start({
              metadata: {
                method: START_METHOD.KEY_DOWN
              }
            });
          }
        }
      }
    },
    view: function view(_view2) {
      editorViewEl = _view2.dom;
      return {
        destroy: function destroy() {
          experience.abort({
            metadata: {
              reason: ABORT_REASON.EDITOR_DESTROYED
            }
          });
        }
      };
    }
  });
};
var isSelectionToolbarAddedInMutation = function isSelectionToolbarAddedInMutation(_ref4) {
  var type = _ref4.type,
    addedNodes = _ref4.addedNodes;
  return type === 'childList' && _toConsumableArray(addedNodes).some(isSelectionToolbarWithinNode);
};
var isSelectionToolbarWithinNode = function isSelectionToolbarWithinNode(node) {
  return containsPopupWithNestedElement(node, '[data-testid="editor-floating-toolbar"]');
};
var isBlockMenuWithinNode = function isBlockMenuWithinNode(node) {
  return containsPopupWithNestedElement(node, '[data-testid="editor-block-menu"]');
};